import { useEffect, useRef, useState } from 'react';
import { Banknote, CreditCard, MapPin, Phone, Printer, UserRound, Wallet } from 'lucide-react';
import Drawer from '../../components/Drawer';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import StatusBadge from '../../components/StatusBadge';
import {
  PAY_LATER_SETTLE_METHODS,
  PAY_LATER_STATUS,
  formatPayLaterAddress,
  mapPayLaterError,
  newClientPaymentId,
  normalizePayLaterItem,
  settlePayLater,
} from '../../api/payLaters';
import { formatCurrency } from '../../data/mockData';
import { payableForTender, payLaterMethodLines, validateSettleAmount } from '../../utils/posMoney';
import { roundMoney } from '../../utils/soldBy';
import form from '../../styles/form.module.css';
import styles from './PayLaterDrawer.module.css';

const METHOD_ICONS = {
  CASH: Banknote,
  UPI: Wallet,
  CARD: CreditCard,
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    timeZone: 'Asia/Kolkata',
  });
}

const STALE_SETTLE_CODES = new Set([
  'already_settled',
  'already_paid',
  'already_voided',
  'not_pay_later',
  'nothing_due',
  'settle_conflict',
]);

export default function PayLaterDrawer({ record, token, onClose, onPaid, onReprint }) {
  const [method, setMethod] = useState('CASH');
  const [collectInput, setCollectInput] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const paymentStamp = useRef({ key: '', id: '', at: '' });

  useEffect(() => {
    if (!record) return;
    setMethod('CASH');
    setCollectInput('');
    setNote('');
    setError('');
    paymentStamp.current = { key: '', id: '', at: '' };
  }, [record]);

  if (!record) return null;

  const customer = record.customer || {};
  const isPending = record.status === PAY_LATER_STATUS.PENDING;
  const outstanding = roundMoney(isPending ? record.outstanding : 0);
  const amountPaid = roundMoney(record.amountPaid);
  const billTotal = roundMoney(record.total);
  const settleCheck = validateSettleAmount(collectInput, outstanding);
  const slice = settleCheck.ok ? settleCheck.amount : 0;
  const cashQuote = payableForTender(slice, 'CASH');
  const cashPayable = method === 'CASH' ? cashQuote.payable : slice;
  const methodLines = payLaterMethodLines(record.paidByMethod);
  const dueAfter = settleCheck.ok ? roundMoney(Math.max(0, outstanding - slice)) : outstanding;

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const paymentIdentity = () => {
    const key = `${slice}|${method}`;
    if (paymentStamp.current.key !== key) {
      paymentStamp.current = {
        key,
        id: newClientPaymentId(),
        at: new Date().toISOString(),
      };
    }
    return paymentStamp.current;
  };

  const handleCollect = async (e) => {
    e?.preventDefault?.();
    if (!isPending || submitting) return;
    if (!settleCheck.ok) {
      setError(mapPayLaterError({ code: settleCheck.code }));
      return;
    }
    const stamp = paymentIdentity();
    setSubmitting(true);
    setError('');
    try {
      const data = await settlePayLater(
        record.orderId || record.id,
        {
          payment_method: method,
          amount: settleCheck.omit ? undefined : settleCheck.amount,
          note: note.trim() || undefined,
          client_payment_id: stamp.id,
          settled_at: stamp.at,
        },
        token,
      );
      const sale = data?.sale || {};
      const receipt = data?.receipt || {};
      const updated = normalizePayLaterItem({
        ...record,
        ...sale,
        ...receipt,
        settlement_method: receipt.settlement_method || sale.settlement_method || method,
        settled_at: receipt.settled_at || sale.settled_at || stamp.at,
      });
      onPaid?.({
        ...data,
        ...updated,
        paidVia: method,
        changeDue: 0,
        cashierNote: note.trim() || undefined,
      });
    } catch (err) {
      setError(mapPayLaterError(err));
      if (STALE_SETTLE_CODES.has(err?.code)) onPaid?.(null);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      open={Boolean(record)}
      onClose={handleClose}
      title={record.displayId || record.id}
      subtitle={`${formatCurrency(isPending ? outstanding : billTotal)} · ${customer.fullName || 'Customer'}`}
      footer={(
        <div className={styles.footerActions}>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Close
          </Button>
          <Button variant="outline" onClick={() => onReprint?.(record)} disabled={submitting}>
            <Printer size={14} /> Reprint
          </Button>
          {isPending && (
            <Button onClick={handleCollect} disabled={submitting || !settleCheck.ok}>
              {submitting ? 'Saving…' : `Collect ${formatCurrency(method === 'CASH' ? cashPayable : slice)} · ${method}`}
            </Button>
          )}
        </div>
      )}
    >
      <div className={styles.metaRow}>
        <StatusBadge status={record.status} size="sm" />
        <span className={styles.metaMuted}>{formatDate(record.createdAt)}</span>
      </div>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Customer</h3>
        <p className={styles.detailLine}>
          <UserRound size={14} /> {customer.fullName || '—'}
        </p>
        <p className={styles.detailLine}>
          <Phone size={14} /> {customer.phone ? `+91 ${customer.phone}` : '—'}
        </p>
        <p className={styles.detailLine}>
          <MapPin size={14} /> {formatPayLaterAddress(customer)}
        </p>
      </section>

      <section className={styles.section}>
        <h3 className={styles.sectionTitle}>Sale</h3>
        <div className={styles.kv}>
          <span>Bill total</span>
          <strong>{formatCurrency(billTotal)}</strong>
        </div>
        <div className={styles.kv}>
          <span>Paid</span>
          <strong>{formatCurrency(amountPaid)}</strong>
        </div>
        <div className={styles.kv}>
          <span>{isPending ? 'Still due' : 'Outstanding'}</span>
          <strong>{formatCurrency(isPending ? outstanding : 0)}</strong>
        </div>
        {methodLines.map((line) => (
          <div key={line.method} className={styles.kv}>
            <span>{line.label}</span>
            <span>{formatCurrency(line.amount)}</span>
          </div>
        ))}
        {record.counter && (
          <div className={styles.kv}>
            <span>Counter</span>
            <span>{record.counter}</span>
          </div>
        )}
        {record.status === PAY_LATER_STATUS.COLLECTED && (
          <>
            <div className={styles.kv}>
              <span>Paid via</span>
              <span>{record.paidVia || '—'}</span>
            </div>
            <div className={styles.kv}>
              <span>Paid at</span>
              <span>{formatDate(record.paidAt)}</span>
            </div>
            {record.settlementAmount != null && (
              <div className={styles.kv}>
                <span>Collected</span>
                <span>{formatCurrency(record.settlementAmount)}</span>
              </div>
            )}
            {record.settlementRoundOff != null && Number(record.settlementRoundOff) !== 0 && (
              <div className={styles.kv}>
                <span>Round off</span>
                <span>{formatCurrency(record.settlementRoundOff)}</span>
              </div>
            )}
          </>
        )}
      </section>

      {isPending && (
        <form className={styles.settleForm} onSubmit={handleCollect}>
          <h3 className={styles.sectionTitle}>Collect</h3>
          {error && <AuthMessage>{error}</AuthMessage>}
          <p className={styles.settleHint}>
            {formatCurrency(outstanding)} is still due. Leave the amount blank to collect all of it, or enter a smaller payment.
          </p>
          <label className={form.field}>
            <span className={form.label}>Amount (optional)</span>
            <div className={styles.tenderedInput}>
              <span>₹</span>
              <input
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder={`Full balance ${outstanding}`}
                aria-label="Amount to collect"
                value={collectInput}
                onChange={(e) => {
                  setCollectInput(e.target.value);
                  setError('');
                }}
                disabled={submitting}
              />
            </div>
          </label>
          {settleCheck.ok && !settleCheck.omit && (
            <p className={styles.settleHint}>
              After this payment, {formatCurrency(dueAfter)} stays due on this bill.
            </p>
          )}
          <div className={styles.methodRow}>
            {PAY_LATER_SETTLE_METHODS.map(({ key, label }) => {
              const Icon = METHOD_ICONS[key] || Wallet;
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.methodBtn} ${method === key ? styles.methodActive : ''}`}
                  onClick={() => setMethod(key)}
                  disabled={submitting}
                >
                  <Icon size={15} /> {label}
                </button>
              );
            })}
          </div>
          {method === 'CASH' && settleCheck.ok && cashQuote.round_off !== 0 && (
            <p className={styles.settleHint}>
              Cash rounds this slice to {formatCurrency(cashPayable)}
              {' '}
              ({cashQuote.round_off > 0 ? '+' : ''}
              {formatCurrency(cashQuote.round_off)}). The amount due stays in paise.
            </p>
          )}
          <label className={form.field}>
            <span className={form.label}>Note (optional)</span>
            <input
              className={form.input}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={200}
              disabled={submitting}
              placeholder="e.g. collected at counter"
            />
          </label>
        </form>
      )}
    </Drawer>
  );
}
